import { Button, ButtonUnstyled, ResizeHandle, Text } from '@sketch/components'
import styled, { css } from 'styled-components'

import { ReactComponent as BellIconSvg } from '@sketch/icons/bell-24'
import { ReactComponent as ArrowUpToLine } from '@sketch/icons/arrow-up-to-line-16'
import { breakpoint } from '@sketch/global-styles'

export const INBOX_SCROLL_CONTAINER_ATTR = 'data-inbox-scroll-container'

export const Wrapper = styled.div`
  display: flex;
  flex: 1;

  /* On mobile we let the page-level container scroll so the sticky header
     can anchor to the viewport; on desktop we clip and the inner panel
     scrolls on its own. */
  ${breakpoint('md')`
    overflow: hidden;
  `}
`

export const ListAndHeaderWrapper = styled.div.attrs({
  [INBOX_SCROLL_CONTAINER_ATTR]: true,
})`
  position: relative;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  padding: 0 20px;
  width: 100%;

  /* Desktop: this panel becomes the scroll container. Mobile: leave overflow
     visible so the sticky header resolves up to the page scroll container. */
  ${({ theme }) => breakpoint('md')`
    overflow-x: hidden;
    overflow-y: auto;
    min-height: 0;
    max-height: 100vh;

    /* Prevent having the sidebar bigger than the actual preview */
    max-width: 33vw;

    border-right: 1px solid ${theme.colors.border.A};
  `}
`

export const ListHeaderWrapper = styled.div<{ $scrolled?: boolean }>(
  ({ theme, $scrolled }) => css`
    display: flex;
    align-items: center;
    gap: 4px;

    position: sticky;
    top: 0;

    /* On mobile the workspace navbar is also sticky at top: 0 (with a higher
       z-index), so offset the inbox header by the navbar height so it sits
       below it rather than being covered. */
    ${breakpoint('base', 'sm')`
      top: ${theme.header.height}px;
    `}

    padding: 23px 0 13px;
    z-index: ${theme.zIndex[1]};

    /* Fade in a backdrop once the list has scrolled, so the title and actions
       stay legible over the scrolling content. The -20px insets cancel
       ListAndHeaderWrapper's horizontal padding so the backdrop bleeds
       edge-to-edge — keep in sync if that padding changes. */
    &::before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: -20px;
      right: -20px;
      z-index: -1;

      backdrop-filter: blur(15px);
      background-color: ${theme.colors.headerSticky.background};
      box-shadow: ${theme.colors.headerSticky.shadow};

      opacity: ${$scrolled ? 1 : 0};
      transition: opacity
        ${`${theme.transitions.duration[1]} ${theme.transitions.timing.easeInOut}`};
    }
  `
)

export const ListHeaderText = styled(Text).attrs({
  textStyle: 'header.primary.H',
})`
  margin-left: 16px;
  margin-right: auto;
`

export const HeaderActions = styled.div<{ $hidden?: boolean }>(
  ({ theme, $hidden }) => css`
    display: flex;
    align-items: center;
    gap: 4px;
    margin-left: auto;
    line-height: 0;
    overflow: hidden;

    transition:
      max-width
        ${`${theme.transitions.duration[2]} ${theme.transitions.timing.easeInOut}`},
      opacity
        ${`${theme.transitions.duration[2]} ${theme.transitions.timing.easeInOut}`};

    max-width: ${$hidden ? '0' : '100px'};
    opacity: ${$hidden ? 0 : 1};

    ${$hidden &&
    css`
      pointer-events: none;
    `}
  `
)

export const InboxHeaderButton = styled(ButtonUnstyled)<{
  $isActive?: boolean
}>(
  ({ theme, $isActive }) => css`
    cursor: pointer;
    width: 40px;
    height: 32px;

    padding: 4px 8px;
    border-radius: ${theme.radii.large};

    background-color: ${$isActive ? theme.colors.sketch.F : undefined};

    svg {
      color: ${$isActive
        ? theme.colors.sketch.A
        : theme.colors.foreground.secondary.D};
    }

    &:hover {
      background-color: ${$isActive
        ? theme.colors.sketch.F
        : theme.colors.state.hover};

      svg {
        color: ${$isActive
          ? theme.colors.sketch.A
          : theme.colors.foreground.secondary.A};
      }
    }
  `
)

export const ListWrapper = styled.ul`
  padding: 0;
  margin: 0;
  flex: 1;
`

export const ListHeader = styled.li`
  padding: 20px 16px 12px;
  list-style: none;

  font-size: ${({ theme }) => theme.fontSizes.D};
  font-weight: 500;
`

export const UpdatePreview = styled.div`
  flex: 1;
  min-width: 0;
  max-height: 100vh;
  overflow-y: auto;
  display: flex;
`

export const EmptyPreview = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 22px;
`

export const EmptyPreviewIcon = styled(BellIconSvg)`
  width: 96px;
  height: 96px;
  color: ${({ theme }) => theme.colors.foreground.secondary.A};
`

export const EmptyPreviewText = styled(Text).attrs({
  textStyle: 'copy.primary.standard.F',
})`
  margin: 0;
  font-weight: 500;
`

export const InboxSearchSlot = styled.div<{ $compact: boolean }>(
  ({ theme, $compact }) => css`
    width: ${$compact ? '173px' : '100%'};
    transition: width
      ${`${theme.transitions.duration[2]} ${theme.transitions.timing.easeInOut}`};
  `
)

/* Floating "X new notifications" badge — sticky inside the inbox list scroll
 * container so it always appears centered over the panel just below the
 * sticky header. Conditionally rendered so it never takes flow space when
 * count is 0. */
export const NewInboxNotificationsBadge = styled(Button).attrs({
  variant: 'primary',
})(
  ({ theme }) => css`
    position: sticky;
    bottom: 40px;

    z-index: ${theme.zIndex[3]};
    margin: auto;
  `
)

export const NewInboxBadgeArrowIcon = styled(ArrowUpToLine)`
  width: 12px;
  height: 12px;
`

export const InboxResizeHandle = styled(ResizeHandle)`
  right: 0;
  width: 6px;

  ::before {
    bottom: 0;
    margin: auto;
    height: 40px;
  }
`
